import React, { useEffect } from 'react'

import { routes } from '@sketch/modules-common'

// Components
import {
  Dropdown,
  LoadingPlaceholder,
  useModalContext,
  ToastLink,
  SelectDropdownSubMenu,
} from '@sketch/components'
import {
  MoveShareToProjectModal,
  RemoveShareFromProjectModal,
} from 'modules/projects'
import { MoveShareToCollectionModal } from 'modules/collections/modals/MoveShareToCollectionModal'
import {
  Chevron,
  SubMenuItem,
  SubMenuTrigger,
} from './ManageProjectAndCollectionActions.styles'

// Hooks
import { useRemoveSharesFromCollection } from 'modules/collections/operations'
import { useToast } from '@sketch/toasts'

// GraphQL
import {
  useGetShareCollectionLazyQuery,
  useGetProjectCollectionsForSelectQuery,
  CollectionForSelectFragment,
  ProjectFragment,
  ShareListItemFragment,
} from '@sketch/gql-types'

// Utils
import { ErrorHandler } from '@sketch/tracing'

// Icons
import { ReactComponent as PlusIcon } from '@sketch/icons/plus-16'
import { ReactComponent as ExclamationMark } from '@sketch/icons/exclamation-mark-16'
import { CreateCollectionModal } from 'modules/collections/modals'

interface Share
  extends Pick<
    ShareListItemFragment,
    | 'identifier'
    | 'name'
    | 'project'
    | 'userCanUpdateSettings'
    | 'publicAccessLevel'
    | 'type'
  > {}

const MoveToProjectAction: React.FC<{ share: Share }> = ({ share }) => {
  const { showModal } = useModalContext()
  return (
    <Dropdown.Item
      onClick={() =>
        showModal(MoveShareToProjectModal, {
          share,
          currentProject: share.project,
        })
      }
    >
      Move To&hellip;
    </Dropdown.Item>
  )
}

const RemoveFromProjectAction: React.FC<{ share: Share }> = ({ share }) => {
  const { showModal } = useModalContext()
  return (
    <Dropdown.Item
      onClick={() => showModal(RemoveShareFromProjectModal, { share })}
    >
      Remove from Project&hellip;
    </Dropdown.Item>
  )
}

const SingleLevelActions: React.FC<{ share: Share }> = ({ share }) => (
  <>
    <MoveToProjectAction share={share} />
    {share.project && <RemoveFromProjectAction share={share} />}
  </>
)

export const CollectionsLoadingState = () => (
  <SubMenuItem
    text="Loading collections…"
    icon={({ className }) => (
      <LoadingPlaceholder className={className} size="16px" />
    )}
    disabled
  />
)

export const CollectionsErrorState = () => (
  <SubMenuItem
    text="Unable to load collections"
    icon={({ className }) => <ExclamationMark className={className} />}
    disabled
  />
)

interface MoveSubMenuProps {
  share: Share
  project: ProjectFragment
  workspaceIdentifier: string
  currentCollection?: CollectionForSelectFragment | null
}

const MoveSubMenu: React.FC<MoveSubMenuProps> = ({
  share,
  project,
  workspaceIdentifier,
  currentCollection,
}) => {
  const { showModal } = useModalContext()
  const { loading, error, data } = useGetProjectCollectionsForSelectQuery({
    variables: { projectIdentifier: project.identifier },
  })

  const handleCreateCollection = () => {
    showModal(CreateCollectionModal, {
      project,
      workspaceIdentifier,
      shareIdentifier: share.identifier,
    })
  }

  const collections =
    data?.project.collectionsUnpaginated
      ?.filter(({ identifier }) => identifier !== currentCollection?.identifier)
      .map(
        collection =>
          ({
            __typename: 'Collection',
            identifier: collection.identifier,
            name: collection.name,
          } as CollectionForSelectFragment)
      ) ?? []

  return (
    <SelectDropdownSubMenu
      value="Move"
      usePortal
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuTrigger
          text="Move"
          data-testid="move-submenu"
          additional={<Chevron />}
          data-hassublevels={true}
          {...props}
        />
      )}
      items={['PROJECT', 'COLLECTION']}
      renderItem={(item, props) => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              text="To Project…"
              data-hassublevels={true}
              onClick={() => {
                showModal(MoveShareToProjectModal, {
                  share,
                  currentProject: share.project,
                })
              }}
            />
          )
        }

        if (loading) {
          return <CollectionsLoadingState />
        }

        if (error) {
          return <CollectionsErrorState />
        }

        if (collections.length) {
          return (
            <SubMenuItem
              text="To Collection…"
              data-hassublevels={true}
              onClick={() => {
                showModal(MoveShareToCollectionModal, {
                  share,
                  collections,
                  handleCreateCollection,
                  project,
                  workspaceIdentifier,
                  currentCollection,
                })
              }}
            />
          )
        }

        return (
          <SubMenuItem
            onClick={handleCreateCollection}
            text="Create Collection…"
            icon={PlusIcon}
          />
        )
      }}
    />
  )
}

interface RemoveSubMenuProps {
  share: Share
  project: ProjectFragment
  collection: CollectionForSelectFragment
  workspaceIdentifier: string
}

const RemoveSubMenu: React.FC<RemoveSubMenuProps> = ({
  share,
  project,
  collection,
  workspaceIdentifier,
}) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()

  const removeSharesFromCollection = useRemoveSharesFromCollection({
    projectIdentifier: project.identifier,
    onCompleted: () => {
      showToast(
        <>
          Document moved to &ldquo;
          <ToastLink
            to={routes.WORKSPACE_PROJECT.create({
              projectId: project.identifier,
              workspaceId: workspaceIdentifier,
            })}
          >
            {project.name}
          </ToastLink>
          &rdquo;
        </>
      )
    },
  })

  return (
    <SelectDropdownSubMenu
      value="Remove"
      usePortal={true}
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuTrigger text="Remove" additional={<Chevron />} {...props} />
      )}
      items={['PROJECT', 'COLLECTION']}
      renderItem={(item, props) => {
        if (item === 'PROJECT') {
          return (
            <SubMenuItem
              text="From Project…"
              onClick={() =>
                showModal(RemoveShareFromProjectModal, { share, collection })
              }
            />
          )
        }

        return (
          <SubMenuItem
            text="From Collection…"
            onClick={() => {
              removeSharesFromCollection({
                variables: {
                  identifier: collection.identifier,
                  shareIdentifiers: [share.identifier],
                },
              })
            }}
          />
        )
      }}
    />
  )
}

interface ManageProjectAndCollectionActionsProps {
  share: Share
  workspaceIdentifier: string
}

const ManageProjectAndCollectionActions: React.FC<ManageProjectAndCollectionActionsProps> = ({
  share,
  workspaceIdentifier,
}) => {
  const [
    getCollection,
    { data, error, loading },
  ] = useGetShareCollectionLazyQuery()

  const shareIdentifier = share.identifier

  useEffect(() => {
    getCollection({ variables: { shareIdentifier } })
  }, [getCollection, shareIdentifier])

  if (loading) {
    const loadingCount = share.project ? 2 : 1
    return <Dropdown.Skeleton count={loadingCount} />
  }

  if (error) {
    ErrorHandler.shouldNeverHappen(
      'Fetching share.collection should never fail'
    )

    return <SingleLevelActions share={share} />
  }

  const maybeProject = share.project
  const maybeCollection = data?.share?.collection

  if (maybeProject) {
    return (
      <>
        <MoveSubMenu
          share={share}
          project={maybeProject}
          workspaceIdentifier={workspaceIdentifier}
          currentCollection={maybeCollection}
        />
        {maybeCollection ? (
          <RemoveSubMenu
            share={share}
            project={maybeProject}
            collection={maybeCollection}
            workspaceIdentifier={workspaceIdentifier}
          />
        ) : (
          <RemoveFromProjectAction share={share} />
        )}
      </>
    )
  }

  return <SingleLevelActions share={share} />
}

export default ManageProjectAndCollectionActions
